import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authApi } from '../services/api';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // Get any stored redirect from registration
  const storedRedirect = localStorage.getItem('postVerifyRedirect');

  useEffect(() => {
    async function verify() {
      if (!token) {
        setStatus('error');
        setErrorMessage('No verification token provided');
        return;
      }

      try {
        await authApi.verifyEmail(token);
        setStatus('success');
        // Clear the stored redirect after successful verification
        // (it will be used in the login link)
      } catch (err) {
        setStatus('error');
        setErrorMessage((err as Error).message || 'Verification failed');
      }
    }

    verify();
  }, [token]);

  // Build login URL with redirect if available
  const loginUrl = storedRedirect
    ? `/login?redirect=${encodeURIComponent(storedRedirect)}`
    : '/login';

  // Clear the stored redirect when navigating to login
  const handleLoginClick = () => {
    localStorage.removeItem('postVerifyRedirect');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <span className="text-6xl">❌</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            Verification Failed
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {errorMessage || 'The verification link is invalid or has expired.'}
          </p>
          <Link to="/register" className="btn-primary mt-6 inline-block">
            Try Again
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full text-center">
        <span className="text-6xl">✅</span>
        <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
          Email Verified!
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Your account has been verified. You can now log in.
        </p>
        {storedRedirect && storedRedirect.includes('/invite/') && (
          <p className="mt-2 text-sm text-primary-600 dark:text-primary-400">
            After logging in, you'll be able to accept the invite.
          </p>
        )}
        <Link
          to={loginUrl}
          onClick={handleLoginClick}
          className="btn-primary mt-6 inline-block"
        >
          Go to Login
        </Link>
      </div>
    </div>
  );
}
